/**
 * The vault passphrase.
 *
 * The app generates it; the user never chooses it. Human-chosen passphrases
 * are the weakest link in any local-first encryption story, and here the
 * passphrase is the *only* mandatory key path — losing it means losing every
 * hint. Six words drawn from a 6906-word list is 76.5 bits, which is well past
 * the point where an offline Argon2id attack is worth anyone's time.
 *
 * The wordlist has every BIP-39 word removed, so a generated passphrase can
 * never be mistaken for a seed phrase. See scripts/gen-wordlist.mjs.
 */

import { randomBelow } from './random.js';
import { EFF_WORDLIST } from './wordlist-eff.js';

export const PASSPHRASE_WORDS = 6;
export const PASSPHRASE_SEPARATOR = '-';

/** Bits of entropy in a generated passphrase. */
export const PASSPHRASE_ENTROPY_BITS = Math.log2(EFF_WORDLIST.length) * PASSPHRASE_WORDS;

export function generatePassphrase(wordCount = PASSPHRASE_WORDS): string[] {
  if (wordCount < 4) throw new Error('passphrase must be at least 4 words');
  const words: string[] = [];
  for (let i = 0; i < wordCount; i++) {
    words.push(EFF_WORDLIST[randomBelow(EFF_WORDLIST.length)]!);
  }
  return words;
}

/**
 * Canonical form for KDF input.
 *
 * Users retype this from paper, so we forgive case, stray whitespace, and
 * whichever separator they remember. What we must never forgive is a different
 * *word*, so normalisation stops at the word boundary.
 */
export function normalisePassphrase(input: string): string {
  return input
    .normalize('NFKD')
    .toLowerCase()
    .split(/[\s\-_.,]+/u)
    .map((w) => w.trim())
    .filter(Boolean)
    .join(PASSPHRASE_SEPARATOR);
}

export function formatPassphrase(words: readonly string[]): string {
  return words.join(PASSPHRASE_SEPARATOR);
}

export interface PassphraseCheck {
  ok: boolean;
  wordCount: number;
  unknownWords: string[];
}

/**
 * Sanity-checks a retyped passphrase *before* spending 64 MiB and a second on
 * Argon2id. A typo caught here becomes "word 3 isn't on the list" instead of
 * "wrong passphrase", which is a far more actionable thing to tell someone
 * squinting at their own handwriting.
 */
export function checkPassphraseShape(input: string): PassphraseCheck {
  const words = normalisePassphrase(input).split(PASSPHRASE_SEPARATOR).filter(Boolean);
  const known = new Set(EFF_WORDLIST);
  const unknownWords = words.filter((w) => !known.has(w));
  return {
    ok: words.length >= 4 && unknownWords.length === 0,
    wordCount: words.length,
    unknownWords,
  };
}

/**
 * Words from the list that start with `prefix`, for the "did you mean" hint on
 * a mistyped word. Never used to autocomplete during entry — that would leak
 * the passphrase to anything watching the DOM.
 */
export function suggestionsFor(prefix: string, limit = 3): string[] {
  const p = prefix.toLowerCase().trim();
  if (p.length < 2) return [];
  const out: string[] = [];
  for (const word of EFF_WORDLIST) {
    if (word.startsWith(p)) {
      out.push(word);
      if (out.length >= limit) break;
    }
  }
  return out;
}
